import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface GuidelineRow {
  readonly brandGuidelineId: string;
  readonly guidelineCode: string;
  readonly title: string;
  readonly guidelineType: string;
  readonly category: string | null;
  readonly version: string;
  readonly status: string;
}

const STATUSES = ['', 'DRAFT', 'UNDER_REVIEW', 'PENDING_APPROVAL', 'APPROVED', 'PUBLISHED', 'ARCHIVED', 'OBSOLETE'];
const GUIDELINE_TYPES = ['', 'BRAND_STANDARD', 'BRAND_MANUAL', 'LOGO_USAGE', 'COLOR_STANDARD', 'TYPOGRAPHY_STANDARD', 'DESIGN_STANDARD', 'COMMUNICATION_STANDARD', 'VISUAL_IDENTITY', 'MARKETING_STANDARD', 'COMPLIANCE_RULE'];
const CREATE_TYPES = ['BRAND_STANDARD', 'BRAND_MANUAL', 'LOGO_USAGE', 'COLOR_STANDARD', 'TYPOGRAPHY_STANDARD', 'DESIGN_STANDARD', 'COMMUNICATION_STANDARD', 'VISUAL_IDENTITY', 'MARKETING_STANDARD', 'COMPLIANCE_RULE'];

/**
 * M36 — Brand Guideline Portal (§31). Register of brand governance guidelines
 * (T483) across the 10 guideline types and the draft → published → obsolete
 * lifecycle. Published guidelines are the authoritative reference against which
 * brand compliance is validated (BR-03). Filterable by guideline type, status
 * and a free-text search. Tenant isolation is enforced server-side (LAW 4).
 */
export function BrandGuidelinePortal(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<GuidelineRow[]>([]);
  const [total, setTotal] = useState(0);
  const [guidelineType, setGuidelineType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState(CREATE_TYPES[0]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (guidelineType) params.set('guidelineType', guidelineType);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: GuidelineRow[]; total: number }>(`/v1/dam/guidelines${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, guidelineType, status, q, reload]);

  const on403 = (err: unknown, perm: string): void => {
    if (err instanceof ApiError && err.status === 403) setNotice(t('m36.common.permRequired', { perm }));
    else setNotice((err as Error).message);
  };

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setNotice(null);
    if (!newTitle.trim()) { setNotice(t('m36.common.titleReq')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/dam/guidelines`, {
        method: 'POST',
        body: { title: newTitle.trim(), guidelineType: newType },
        token,
      });
      setNotice(t('m36.brandGuideline.created', { name: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      on403(err, 'M36_GUIDELINE_MANAGE');
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: GuidelineRow, action: string, label: string, perm: string): Promise<void> => {
    setNotice(null);
    setBusyId(r.brandGuidelineId);
    try {
      await apiRequest(`/v1/dam/guidelines/${r.brandGuidelineId}/${action}`, { method: 'POST', body: {}, token });
      setNotice(t('m36.common.codeLabel', { code: r.guidelineCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      on403(err, perm);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <main aria-label="brand-guideline-portal">
      <h1>{t('m36.brandGuideline.title')}</h1>
      <form aria-label="create-guideline-form" onSubmit={(e) => void create(e)}>
        <label>
          {t('m36.common.title')}
          <input aria-label="field-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
        </label>
        <label>
          {t('m36.common.type')}
          <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
            {CREATE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <Button variant="primary" type="submit" aria-label="create-guideline" disabled={busy}>{t('m36.brandGuideline.create')}</Button>
      </form>
      {notice && <p role="status" aria-label="guideline-notice">{notice}</p>}
      <div className="filters">
        <label>
          {t('m36.common.type')}
          <select aria-label="filter-type" value={guidelineType} onChange={(e) => setGuidelineType(e.target.value)}>
            {GUIDELINE_TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m36.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m36.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('common.search')}
          <input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m36.common.titleKeywordPh')} />
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="guideline-count">{t('m36.brandGuideline.count', { count: total })}</p>
          <DataTable<GuidelineRow>
            ariaLabel="guideline-table"
            testId="guideline-table"
            rowKey={(r) => r.brandGuidelineId}
            rowDataAttrs={(r) => ({ 'data-status': r.status })}
            rows={rows}
            columns={[
              { key: 'code', header: t('m36.common.code'), render: (r) => r.guidelineCode },
              { key: 'title', header: t('m36.common.title'), render: (r) => r.title },
              { key: 'type', header: t('m36.common.type'), render: (r) => r.guidelineType },
              { key: 'category', header: t('m36.common.category'), render: (r) => r.category ?? '' },
              { key: 'version', header: t('m36.common.version'), render: (r) => r.version },
              { key: 'status', header: t('m36.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m36.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`submit-${r.guidelineCode}`} disabled={busyId === r.brandGuidelineId} onClick={() => void act(r, 'submit', t('m36.brandGuideline.act.submitted'), 'M36_GUIDELINE_MANAGE')}>{t('m36.common.submit')}</Button>
                  )}
                  {(r.status === 'UNDER_REVIEW' || r.status === 'PENDING_APPROVAL') && (
                    <Button variant="secondary" type="button" aria-label={`approve-${r.guidelineCode}`} disabled={busyId === r.brandGuidelineId} onClick={() => void act(r, 'approve', t('m36.brandGuideline.act.approved'), 'M36_GUIDELINE_APPROVE')}>{t('m36.common.approve')}</Button>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="primary" type="button" aria-label={`publish-${r.guidelineCode}`} disabled={busyId === r.brandGuidelineId} onClick={() => void act(r, 'publish', t('m36.brandGuideline.act.published'), 'M36_GUIDELINE_APPROVE')}>{t('m36.common.publish')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
